import React, { useState, useMemo } from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Eye } from 'lucide-react';
import { useToast } from '@shared/hooks/use-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMeterRuns } from '../hook';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { PAGE_SIZE } from '@shared/utils/constants';
import BadgeList from '@shared/components/BadgeList';

interface ReadsTableProps {
  methodFilter?: 'Manual' | 'Automated' | 'Mobile';
}

export const ReadsTable: React.FC<ReadsTableProps> = ({ methodFilter }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const limit = PAGE_SIZE;

  const utilityId = getRemoteUtilityId();
  const { data: meterRuns, isLoading } = useMeterRuns(currentPage, utilityId, methodFilter, limit, currentSearch);

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    newParams.page = "1";
    setSearchParams(newParams);
  };

  const pagination = meterRuns ? {
    currentPage: currentPage,
    totalPages: Math.max(1, Math.ceil(meterRuns.count / limit)),
    totalItems: meterRuns.count,
    itemsPerPage: limit,
    startItem: meterRuns.count > 0 ? (currentPage - 1) * limit + 1 : 0,
    endItem: Math.min(currentPage * limit, meterRuns.count),
  } : undefined;

  const mappedData = useMemo(() => {
    return (meterRuns?.results ?? []).map((row: any) => ({
      ...row,
      mappedRunDate: row.createdDate || row.lastRunDate || 'NA',
      mappedCycleCode: row.meterSchedule?.meterCycle?.meterCycleName || 'NA',
      mappedUtilityService: row.meterSchedule?.meterCycle?.productCode?.join(', ') || 'NA',
      mappedResourceType: row.resourceTypeDisplay || 'NA',
      mappedSubType: row.resourceSubTypeDisplay || 'NA',
      mappedRoutes: row.meterSchedule?.meterCycle?.routeCount || 'NA',
      mappedResources: row.summary?.meterCount || 'NA',
      mappedRunTime: row.startDatetime || 'NA',
      total: row.summary?.meterCount || 'NA',
      collected: row.summary?.readingCount || 'NA',
      pending: row.summary?.pendingCount || 'NA',
    }));
  }, [meterRuns?.results]);

  const columns: TableColumn<any>[] = [
    {
      key: 'mappedRunDate',
      header: 'Run Date',
    },
    {
      key: 'mappedCycleCode',
      header: 'Cycle',
    },
    {
      key: 'dataCollectionMethodName',
      header: 'Method',
      render: (val) => {
        return (
          <Badge variant="outline" className={`font-medium text-xs rounded-full px-2.5 py-0.5`}>
            {val}
          </Badge>
        );
      },
    },
    {
      key: 'mappedUtilityService',
      header: 'Utility Service',
    },
    {
      key: 'mappedResourceType',
      header: 'Resource Type',
       render(value) {
        return (
          <BadgeList items={value}/>
        );
      },
    },
    {
      key: 'mappedSubType',
      header: 'Sub Type',
      render(value) {
        return (
          <BadgeList items={value}/>
        );
      },
    },
    {
      key: 'mappedResources',
      header: 'Resources',
    },
    {
      key: 'total',
      header: 'Total',
    },
    {
      key: 'collected',
      header: 'Collected',
    },
    {
      key: 'pending',
      header: 'Pending',
    },
    {
      key: 'mappedRunTime',
      header: 'Run Time',
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, row) => {
        const val = row.status || 'Pending';
        const isComplete = val === 'Completed';
        return (
          <Badge
            variant="outline"
            className={`font-medium text-xs rounded-full px-2.5 py-0.5 ${isComplete
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-blue-50 text-blue-700 border-blue-200'
              }`}
          >
            {val}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, row) => (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-gray-100"
          onClick={() => {
            if (!row.dataCollectionMethodName) {
              toast({
                title: "Data Collection Method Not Found",
                description: "This record is missing a data collection method.",
                variant: "destructive",
              });
              return;
            }
            navigate(`/mx/reads/detail/${row.dataCollectionMethodName.toLowerCase()}/${row.id}`);
          }}
        >
          <Eye className="h-4 w-4 text-gray-400" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4 pt-2">
      <GenericTable
        data={mappedData}
        columns={columns}
        rowKey="id"
        emptyMessage="No reads found."
        searchPlaceholder="Search by run ID, cycle, utility, type..."
        onSearch={handleSearch}
        loading={isLoading}
        pagination={pagination}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
